// PaginationCompact.js — Round 3 UI fixes (2026-06-07) — Fix 2.
//
// Paginación de 2 líneas con ventana visible de 5-7 números + elipsis.
//   Línea 1: "Página X de Y"
//   Línea 2: « ‹  1 ... [N-2] [N-1] [N] [N+1] [N+2] ... Y  › »
//
// Reglas:
//   - Ventana central: hasta 5 páginas (N-2..N+2) si caben.
//   - Si la ventana no toca el extremo (página 1 o Y), insertar elipsis.
//   - Siempre se muestra primera y última si totalPages > 1.
//   - Cap superior: 7 botones numerados visibles a la vez.

import React from 'react';
import './PaginationCompact.css';

function buildPageWindow(page, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items = [];
  const windowSize = 2; // 2 a cada lado de page → 5 páginas
  const left = Math.max(2, page - windowSize);
  const right = Math.min(totalPages - 1, page + windowSize);

  items.push(1);
  if (left > 2) items.push('ellipsis-left');
  for (let p = left; p <= right; p++) items.push(p);
  if (right < totalPages - 1) items.push('ellipsis-right');
  items.push(totalPages);
  return items;
}

export default function PaginationCompact({
  page,           // 1-based
  totalPages,
  onChange,       // (newPage: number) => void
  position = 'top', // 'top' | 'bottom' — sólo afecta border styling
}) {
  if (!totalPages || totalPages <= 1) return null;

  const safePage = Math.max(1, Math.min(page, totalPages));
  const disablePrev = safePage <= 1;
  const disableNext = safePage >= totalPages;

  const pageItems = buildPageWindow(safePage, totalPages);

  return (
    <div className={`tm-pag tm-pag-${position}`}>
      <div className="tm-pag-info">
        Página <span className="tm-pag-num">{safePage}</span> de{' '}
        <span className="tm-pag-num">{totalPages}</span>
      </div>
      <div className="tm-pag-controls">
        <button
          type="button"
          className="tm-pag-btn"
          onClick={() => onChange(1)}
          disabled={disablePrev}
          title="Primera página"
          aria-label="Primera página"
        >
          «
        </button>
        <button
          type="button"
          className="tm-pag-btn"
          onClick={() => onChange(safePage - 1)}
          disabled={disablePrev}
          title="Página anterior"
          aria-label="Página anterior"
        >
          ‹
        </button>
        {pageItems.map((item, i) => {
          if (item === 'ellipsis-left' || item === 'ellipsis-right') {
            return (
              <span key={item + i} className="tm-pag-ellipsis" aria-hidden="true">…</span>
            );
          }
          const isCurrent = item === safePage;
          return (
            <button
              key={item}
              type="button"
              className={`tm-pag-btn tm-pag-num-btn${isCurrent ? ' is-current' : ''}`}
              onClick={() => onChange(item)}
              disabled={isCurrent}
              aria-current={isCurrent ? 'page' : undefined}
              aria-label={`Ir a página ${item}`}
            >
              {item}
            </button>
          );
        })}
        <button
          type="button"
          className="tm-pag-btn"
          onClick={() => onChange(safePage + 1)}
          disabled={disableNext}
          title="Página siguiente"
          aria-label="Página siguiente"
        >
          ›
        </button>
        <button
          type="button"
          className="tm-pag-btn"
          onClick={() => onChange(totalPages)}
          disabled={disableNext}
          title="Última página"
          aria-label="Última página"
        >
          »
        </button>
      </div>
    </div>
  );
}
